import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as styles from './data-pipeline-run.module.scss';
import { AiCheckBox, BasicButton } from '../../../../../assets/common';
import { Empty, Pagination, Spin } from 'antd';
import moment from 'moment';
import { dropdown_builder } from '../../../../../assets/img';
import { auditLogApi } from '../../../../../services/auditLog';
import { getNoByIndex, paginationConfig } from '../../../../../utils/helper';
import { cloneDeep } from 'lodash';
import { DATE_TIME_FORMAT } from '../../../../../utils/constants';
import { useSelector } from 'react-redux';
import RangeCalendar from '../../../../../components/RangeCalendar/RangeCalendar';

const LIMIT_ROWS = 15;
const STATUS_STYLE = {
  Failed: { background: '#FBE2E2', color: '#A91A1F' },
  InProgress: { background: '#FEF2DC', color: '#C4861C' },
  Cancelled: { background: '#F5F5F5', color: '#575757' },
  Succeeded: { background: '#E7F4E8', color: '#096924' },
};
const INIT_DATA = { data: [], total: 0 };
const INT_APPLIED_RANGE = { type: '', range: [null, null] };

const DataPipelineRun = ({ page }) => {
  const [data, setData] = useState(INIT_DATA);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: Number(page) || 1,
    pipeline: null,
    start_time: null,
    end_time: null,
    status: null,
    limit: LIMIT_ROWS,
  });
  const [expandIds, setExpandIds] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    pipeline: null,
    status: null,
  });
  const [appliedRange, setAppliedRange] = useState(INT_APPLIED_RANGE);
  const { roleId } = useSelector((state) => state.user.roleActive);

  const pagination = useMemo(() => {
    const onChange = (page) => setFilter((prev) => ({ ...prev, page }));
    return paginationConfig(data.total, filter.page, onChange, LIMIT_ROWS);
  }, [data.total, filter.page]);

  const handleApply = (filterName, selected) => {
    setFilter((prev) => ({
      ...prev,
      page: 1,
      [filterName]: selected,
    }));
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await auditLogApi.getDataPipelineRun({ time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone, ...filter }, roleId);
      if (res.status === 200) {
        setData(res.data.result);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filter, roleId]);

  useEffect(() => {
    const fetchFilter = async () => {
      try {
        setLoading(true);
        const res = await auditLogApi.getFilterDataPipelineRun();
        if (res.status === 200) {
          const result = res.data.result.data;
          const filters = {
            pipeline: result.pipelines,
            status: result.statuses,
          };

          setFilter((prev) => ({
            ...prev,
            ...filter,
          }));
          setFilterOptions(filters);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchFilter();
  }, []);

  const [renderCount, setRenderCount] = useState(0);
  useEffect(() => {
    setRenderCount((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!filterOptions.pipeline && !filterOptions.status) return;
    fetchData();
  }, [fetchData, filterOptions]);

  useEffect(() => {
    if (renderCount === 0) return;
    const is1RangeApplied = appliedRange.type && (!appliedRange.range[0] || !appliedRange.range[1]);
    const range = { start_time: null, end_time: null };
    if (is1RangeApplied) {
      range[appliedRange.type] = appliedRange.range[0].toISOString();
    } else {
      range.start_time = appliedRange.range[0] ? appliedRange.range[0].toISOString() : null;
      range.end_time = appliedRange.range[1] ? appliedRange.range[1].toISOString() : null;
    }
    setFilter((prev) => ({
      ...prev,
      page: 1,
      ...range,
    }));
  }, [appliedRange]);

  const handleClickExpand = (id) => {
    const newExpandIds = cloneDeep(expandIds);
    if (newExpandIds.includes(id)) {
      setExpandIds(newExpandIds.filter((expandId) => expandId !== id));
      return;
    }
    newExpandIds.push(id);
    setExpandIds(newExpandIds);
  };

  return (
    <Spin spinning={loading}>
      <div className={styles.wrapper}>
        <div className={styles.title}>Data Pipeline Run</div>
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>Activity</th>
              <th>
                <Dropdown
                  options={filterOptions.pipeline}
                  onApply={(selected) => {
                    handleApply('pipeline', selected);
                  }}
                >
                  <span>Pipeline</span>
                </Dropdown>
              </th>
              <th>
                <RangePicker appliedRange={appliedRange} setAppliedRange={setAppliedRange} type="start_time">
                  Start Date & Time
                </RangePicker>
              </th>
              <th>
                <RangePicker appliedRange={appliedRange} setAppliedRange={setAppliedRange} type="end_time">
                  End Date & Time
                </RangePicker>
              </th>
              <th>
                <Dropdown
                  options={filterOptions.status}
                  onApply={(selected) => {
                    handleApply('status', selected);
                  }}
                >
                  <span>Status</span>
                </Dropdown>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.total ? (
              data.data.map((r, index) => {
                const isExpand = expandIds.includes(r.id);
                const expandClass = isExpand ? styles.expand : '';
                return (
                  <React.Fragment key={r.id}>
                    <tr className={expandClass}>
                      <td>{getNoByIndex(LIMIT_ROWS, filter.page, index)}</td>
                      <td>{r.activity || '-'}</td>
                      <td>{r.pipeline || '-'}</td>
                      <td>{r.start_time ? moment(new Date(r.start_time)).format(DATE_TIME_FORMAT.DATE_TIME_SLASH) : '-'}</td>
                      <td>{r.end_time ? moment(new Date(r.end_time)).format(DATE_TIME_FORMAT.DATE_TIME_SLASH) : '-'}</td>
                      <td>
                        <span style={STATUS_STYLE[r.status]}>{r.status}</span>
                        <div>{r.status_details}</div>
                      </td>
                      <td>
                        {!!r.groups.length && (
                          <BasicButton mode="icon" onClick={() => handleClickExpand(r.id)}>
                            <img src={dropdown_builder} alt="expand" className={expandClass} />
                          </BasicButton>
                        )}
                      </td>
                    </tr>
                    {isExpand &&
                      r.groups.map((item) => (
                        <tr key={item.id} className={styles.childRecord}>
                          <td></td>
                          <td></td>
                          <td>{item.pipeline || '-'}</td>
                          <td>{item.start_time ? moment(new Date(item.start_time)).format(DATE_TIME_FORMAT.DATE_TIME_SLASH) : '-'}</td>
                          <td>{item.end_time ? moment(new Date(item.end_time)).format(DATE_TIME_FORMAT.DATE_TIME_SLASH) : '-'}</td>
                          <td>
                            <span style={STATUS_STYLE[item.status]}>{item.status}</span>
                            <div>{item.status_details}</div>
                          </td>
                          <td></td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan="6">
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="mt-3">
          <Pagination {...pagination} />
        </div>
      </div>
    </Spin>
  );
};

export default DataPipelineRun;

const Dropdown = ({ children, options, onApply }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [preSelected, setPreSelected] = useState([]);
  const ref = useRef();

  const handleCheck = (checked, option) => {
    let newSelected = cloneDeep(selected);
    newSelected = checked ? [...newSelected, option] : newSelected.filter((item) => item !== option);
    setSelected(newSelected);
  };

  const handleCheckAll = (checked) => {
    if (checked) {
      setSelected(options);
      return;
    }
    setSelected([]);
  };

  const handleApply = () => {
    onApply(selected);
    setPreSelected(selected);
    setOpen(false);
  };

  useEffect(() => {
    if (options) setSelected(options);
  }, [options]);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSelected(preSelected);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [open]);

  return (
    <div className={styles.dropdownWrapper} ref={ref}>
      <div className={styles.clickField} onClick={() => setOpen(true)}>
        {children}
        <span className={styles.arrowDown} />
      </div>
      <div className={styles.dropdown} style={open ? { zIndex: 10, opacity: 1 } : { zIndex: -10, opacity: 0 }}>
        <Spin spinning={!options}>
          <div className={styles.list}>
            <div className={styles.showLabel}>Show</div>
            <div className={styles.row}>
              <AiCheckBox onChange={(e) => handleCheckAll(e.target.checked)} checked={selected?.length === options?.length} /> All
            </div>
            {options?.map((option) => (
              <div key={option} className={styles.row}>
                <AiCheckBox checked={selected.includes(option)} onChange={(e) => handleCheck(e.target.checked, option)} /> {option}
              </div>
            ))}
          </div>
        </Spin>
        <div className={styles.btnRow}>
          <BasicButton onClick={() => setSelected([])}>Clear</BasicButton>
          <BasicButton mode="teal" onClick={() => handleApply()}>
            Apply
          </BasicButton>
        </div>
      </div>
    </div>
  );
};

const RangePicker = ({ children, appliedRange, setAppliedRange, type }) => {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState([null, null]);

  const ref = useRef();

  const handleOnChange = (value) => {
    setRange(value);
  };

  const handleApply = () => {
    setAppliedRange({ type, range });
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleResetDate = () => {
    setAppliedRange(INT_APPLIED_RANGE);
    setOpen(false);
  };

  useEffect(() => {
    setRange(appliedRange.range);
  }, [open]);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [open]);

  return (
    <div className={styles.datePicker} ref={ref}>
      <div className={styles.clickField} onClick={() => setOpen(true)}>
        {children}
        <span className={styles.arrowDown} />
      </div>
      <div className={styles.dropdown} style={open ? { zIndex: 10, opacity: 1 } : { zIndex: -10, opacity: 0 }}>
        <RangeCalendar value={range} onChange={handleOnChange}>
          <div className={styles.footer}>
            <BasicButton mode="icon" className={styles.resetDate} onClick={handleResetDate}>
              Reset Dates
            </BasicButton>
            <div>
              <BasicButton onClick={handleCancel}>Cancel</BasicButton>
              <BasicButton mode="teal" onClick={handleApply}>
                Apply
              </BasicButton>
            </div>
          </div>
        </RangeCalendar>
      </div>
    </div>
  );
};
